import { type FC } from 'react';
import { defineMessages, useIntl, type MessageDescriptor } from 'dibs-react-intl';
import { type InitiateConnectionResult } from 'dibs-crypto-utils/exports/useWeb3Connection.ts';
import { Button, type ButtonProps } from 'dibs-elements/exports/Button.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import WalletConnectIcon from 'dibs-icons/exports/legacy/WalletConnect.ts';

import styles from './ConnectWalletConnectButton.scss';

const messages = defineMessages({
    connectWalletConnect: {
        id: 'connectWalletConnect.cta.connectWalletConnect',
        defaultMessage: 'WalletConnect',
    },
    openWalletConnect: {
        id: 'connectWalletConnect.cta.openWalletConnect',
        defaultMessage: 'Open WalletConnect',
    },
    connecting: {
        id: 'connectWalletConnect.cta.connecting',
        defaultMessage: 'Connecting',
    },
});

function getCtaMessage(useOpenCopy: boolean, connecting?: boolean): MessageDescriptor {
    if (useOpenCopy) {
        return messages.openWalletConnect;
    } else if (connecting) {
        return messages.connecting;
    } else {
        return messages.connectWalletConnect;
    }
}

type Props = {
    fullWidth?: boolean;
    useOpenCopy?: boolean;
    connected?: InitiateConnectionResult;
    onConnect?: () => Promise<void> | void;
    size?: ButtonProps['size'];
    disabled?: boolean;
};

export const ConnectWalletConnectButton: FC<Props> = ({
    connected = {},
    onConnect,
    fullWidth,
    size,
    useOpenCopy = false,
    disabled = false,
}) => {
    const intl = useIntl();

    const connectToWalletConnect = async (): Promise<void> => {
        if (connected.connecting) {
            return;
        }
        if (onConnect) {
            await onConnect();
        }
    };

    const ctaMessage = getCtaMessage(useOpenCopy, connected.wcConnecting);

    return (
        <Button
            size={size}
            dataTn="connect-walletconnect-cta"
            onClick={connectToWalletConnect}
            disabled={connected.connecting || disabled}
            fullWidth={fullWidth}
            type={connected.connecting || disabled ? 'primary' : 'transparent'}
        >
            <div className={styles.buttonContent}>
                <div className={styles.iconContainer}>
                    <WalletConnectIcon />
                </div>
                {intl.formatMessage(ctaMessage)}
                {connected.wcConnecting && (
                    <Spinner size="tiny" black containerClass={styles.spinnerContainer} />
                )}
            </div>
        </Button>
    );
};
